import * as Sentry from '@sentry/react';

import { UPDATE_METADATA } from 'queries/update_metadata';
import { authenticatedPost } from 'utils/axios';
import { isPlainObject } from 'utils/parseUtils';

import type { UpdateMetadataMutationResultType, UserMerchantPreferencesBag } from 'model/types/user-merchant-preferences';

function isUpdateMetadataResult(value: unknown): value is UpdateMetadataMutationResultType {
  return isPlainObject(value) && (value.updateMetadata === null || isPlainObject(value.updateMetadata));
}

function isSuccessfulUpdate(response: unknown): boolean {
  if (!isPlainObject(response) || (Array.isArray(response.errors) && response.errors.length > 0)) {
    return false;
  }

  const data: unknown = response.data;
  return isUpdateMetadataResult(data) && data.updateMetadata?.success === true;
}

export async function persistBag({ roleId, payload }: { roleId: string; payload: UserMerchantPreferencesBag }): Promise<boolean> {
  const metadata = JSON.stringify(payload);

  // The backend reads `{}` as "clear the whole bag", so a payload that serialises away to nothing —
  // an `undefined` value is the reachable case — would wipe every other feature's namespace.
  if (metadata === '{}') {
    Sentry.captureException(new Error('refused to persist an empty user-merchant preferences payload'));
    return false;
  }

  try {
    const response: unknown = await authenticatedPost({
      query: UPDATE_METADATA,
      vars: { input: { id: roleId, metadata } }
    });

    if (!isSuccessfulUpdate(response)) {
      Sentry.captureException(new Error('updateMetadata did not report success while persisting user-merchant preferences'));
      return false;
    }

    return true;
  } catch (error) {
    // a failed write leaves the optimistic in-memory value in place and never reaches the caller
    Sentry.captureException(error);
    return false;
  }
}
